import { useEffect, useRef, useState } from 'react';
import { Icon } from '../../ui/index.js';
import type { SortDir, SortKey, SortState } from './photoSort.js';

interface Props {
  value: SortState;
  onChange: (next: SortState) => void;
}

interface Option {
  key: SortKey;
  dir: SortDir;
  label: string;
  icon: string;
}

const OPTIONS: Option[] = [
  { key: 'uploadedAt', dir: 'desc', label: 'Più recenti', icon: 'arrow_downward' },
  { key: 'uploadedAt', dir: 'asc', label: 'Meno recenti', icon: 'arrow_upward' },
  { key: 'rating', dir: 'desc', label: 'Più valutate', icon: 'star' },
  { key: 'rating', dir: 'asc', label: 'Meno valutate', icon: 'star_border' },
];

function labelFor(value: SortState): Option {
  return OPTIONS.find((o) => o.key === value.key && o.dir === value.dir) ?? OPTIONS[0]!;
}

// Pill-style sort selector. Photos-app-ish: single chip with current label,
// tap opens a small popover with the four presets. No radio toolbar.
export function PhotoSortControl({ value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const current = labelFor(value);

  useEffect(() => {
    if (!open) return;
    function onClick(ev: MouseEvent) {
      if (!wrapRef.current?.contains(ev.target as Node)) setOpen(false);
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 rounded-full border border-outline bg-surface-container-lowest px-3 py-1.5 text-xs font-medium text-on-surface transition-colors hover:border-on-surface-variant/40"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Icon name="sort" className="text-base text-on-surface-variant" />
        <span>{current.label}</span>
        <Icon name="expand_more" className="text-base text-on-surface-variant" />
      </button>
      {open ? (
        <ul
          role="listbox"
          className="absolute right-0 z-20 mt-2 w-52 overflow-hidden rounded-2xl border border-outline bg-surface-container shadow-elevated"
        >
          {OPTIONS.map((opt) => {
            const active = opt.key === value.key && opt.dir === value.dir;
            return (
              <li key={`${opt.key}-${opt.dir}`}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    onChange({ key: opt.key, dir: opt.dir });
                    setOpen(false);
                  }}
                  className={[
                    'flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors',
                    active
                      ? 'bg-cyan/10 text-cyan'
                      : 'text-on-surface hover:bg-surface-container-low',
                  ].join(' ')}
                >
                  <Icon
                    name={opt.icon}
                    className={`text-base ${active ? 'text-cyan' : 'text-on-surface-variant'}`}
                  />
                  <span className="flex-1">{opt.label}</span>
                  {active ? <Icon name="check" className="text-base text-cyan" /> : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
